/**
 * GatedScreen — ecranul pentru userii neautorizati.
 * Editorial + camera scan QR cu animatie la detectare.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EVENT_NAME, EVENT_DATE } from '@/lib/supabase';
import { QrScanner } from './QrScanner';
import { HomePageBackground } from '@/components/ui/PageBackground';
import { useInAppBrowser, tryOpenInExternalBrowser } from '@/hooks/useInAppBrowser';
import type { AuthResult } from '@/hooks/useInvitationGate';

interface GatedScreenProps {
  /** Apelata cand userul scaneaza un QR valid. */
  onAuthorize: (input: string) => Promise<AuthResult>;
}

const ERROR_COPY: Record<string, { title: string; body: string }> = {
  invalid: {
    title: 'Cod QR invalid',
    body: 'Acest cod nu e recunoscut. Încearcă din nou cu codul de pe masă.',
  },
  expired: {
    title: 'Cod expirat',
    body: 'Acest cod nu mai e valabil. Cere unul nou de la organizatori.',
  },
  consumed: {
    title: 'Cod epuizat',
    body: 'Toate intrările pentru acest cod au fost folosite.',
  },
  error: {
    title: 'Eroare de conexiune',
    body: 'Nu am putut verifica codul. Încearcă din nou.',
  },
};

export function GatedScreen({ onAuthorize }: GatedScreenProps) {
  const [feedback, setFeedback] = useState<AuthResult | null>(null);
  const [openStatus, setOpenStatus] = useState<string | null>(null);
  const inApp = useInAppBrowser();

  const handleScan = async (input: string) => {
    const result = await onAuthorize(input);
    setFeedback(result);
    // Auto-reset feedback-ul dupa 4 secunde ca userul poate scana din nou
    if (!result.ok) {
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleOpenExternal = () => {
    const res = tryOpenInExternalBrowser();
    setOpenStatus(res.message);
    setTimeout(() => setOpenStatus(null), 5000);
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-md py-xl">
      <HomePageBackground />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md text-center"
      >
        {/* Banner in-app browser — apare doar dacă userul e în Instagram/Facebook/etc */}
        <AnimatePresence>
          {inApp.inApp && inApp.hint && (
            <motion.div
              key="inapp-banner"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="mb-lg rounded-md border border-accent/40 bg-accent/10 p-sm text-left"
              role="status"
            >
              <p className="font-serif text-sm font-medium text-fg">
                Deschide în browserul extern pentru cea mai bună experiență
              </p>
              <p className="mt-xs text-xs leading-relaxed text-fg-soft">
                {inApp.hint}
              </p>
              <button
                type="button"
                onClick={handleOpenExternal}
                className="mt-sm btn-secondary px-md py-1.5 text-xs"
              >
                Deschide în Safari/Chrome
              </button>
              <AnimatePresence>
                {openStatus && (
                  <motion.p
                    key="open-status"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-xs text-[11px] text-fg-soft"
                  >
                    {openStatus}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Eyebrow */}
        <p className="eyebrow text-fg-soft">{EVENT_DATE}</p>

        {/* Title */}
        <h1 className="mt-sm font-serif text-h3 text-fg">{EVENT_NAME}</h1>

        {/* Decorative divider */}
        <div className="mx-auto mt-lg h-px w-16 bg-border" aria-hidden="true" />

        {/* Body */}
        <p className="mt-lg font-serif text-base italic leading-relaxed text-fg-soft">
          Această galerie e privată.
        </p>
        <p className="mt-md text-sm leading-relaxed text-fg">
          Scanează codul QR de pe masă ca să intri.
        </p>

        {/* Scanner */}
        <div className="mt-lg">
          <QrScanner onScan={handleScan} hint="Apasă butonul și îndreaptă camera spre cod" />
        </div>

        {/* Feedback (error/success) */}
        <AnimatePresence>
          {feedback && !feedback.ok && feedback.reason && (
            <motion.div
              key="err"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="mt-md rounded-md border border-error/40 bg-error/10 p-sm text-left"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-error">
                {ERROR_COPY[feedback.reason].title}
              </p>
              <p className="mt-xs text-sm text-fg">
                {ERROR_COPY[feedback.reason].body}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </main>
  );
}
